import { useState } from 'react'
import { PiCaretDown } from 'react-icons/pi'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'

import { cn } from 'components/UI/cn'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'

interface FormatToggleProps {
  plainText: boolean
  onChange: (plainText: boolean) => void
  disabled?: boolean
  className?: string
}

const FORMAT_OPTIONS: Array<{
  value: boolean
  title: string
  description: string
}> = [
  {
    value: true,
    title: 'Plain text (recommended)',
    description: 'Best deliverability for cold outreach.',
  },
  {
    value: false,
    title: 'HTML',
    description: 'For warm comms, rebate links, GIFs, transactional.',
  },
]

/**
 * Plain-text / HTML selector for the Build page. The trigger is a small pill
 * that shows the current mode; clicking it opens the rich two-card selector
 * with descriptions. Controlled: the parent owns the state and decides when
 * to persist (see FIR-7351).
 */
export function FormatToggle({
  plainText,
  onChange,
  disabled,
  className,
}: FormatToggleProps) {
  const [open, setOpen] = useState(false)

  const handleChange = (nextPlainText: boolean) => {
    setOpen(false)
    if (nextPlainText === plainText || disabled) return
    onChange(nextPlainText)
  }

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <FbButton
          type="button"
          variant="secondary"
          size="xs"
          disabled={disabled}
          aria-label="Email format"
          className={cn('gap-1', className)}
        >
          {plainText ? 'Plain text' : 'HTML'}
          <PiCaretDown className="size-3" />
        </FbButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[360px] space-y-2 p-3"
      >
        <div className="px-1">
          <p className="text-xs uppercase tracking-wider text-fg-secondary">
            Email format
          </p>
          <p className="mt-0.5 text-xs text-fg-secondary">
            Applies to every step in this campaign.
          </p>
        </div>
        <div className="grid gap-2">
          {FORMAT_OPTIONS.map((option) => {
            const isSelected = option.value === plainText
            return (
              <button
                key={option.title}
                type="button"
                onClick={() => handleChange(option.value)}
                disabled={disabled}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-all',
                  isSelected
                    ? 'border-brand-bright bg-basil-50'
                    : 'border-line hover:border-brand-bright/40',
                  disabled && 'cursor-not-allowed opacity-60'
                )}
              >
                <span
                  className={cn(
                    'mt-1 inline-block size-3 shrink-0 rounded-full border-2',
                    isSelected
                      ? 'border-brand-bright bg-brand-bright'
                      : 'border-ds2026-border-tertiary bg-surface'
                  )}
                />
                <span className="flex-1">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      isSelected ? 'text-brand-active' : 'text-fg'
                    )}
                  >
                    {option.title}
                  </span>
                  <span className="mt-0.5 block text-xs text-fg-secondary">
                    {option.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        {!plainText && (
          <Alert
            color="warning"
            description="HTML can hurt deliverability for cold outreach. Stick with plain text unless you need formatting (rebate links, GIFs, transactional)."
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
